import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { adminApi, adminKey } from "../api/admin";
import { useSession } from "../session/SessionContext";

/** Whether we're still validating a stored key, signed in, or signed out. */
type AuthStatus = "checking" | "authed" | "anon";

interface AdminAuthValue {
  status: AuthStatus;
  /** Sign in with the seeded admin credentials, then store the returned key. */
  login: (email: string, password: string) => Promise<void>;
  /** Forget the key and return to signed-out. */
  logout: () => void;
}

const Ctx = createContext<AdminAuthValue | null>(null);

/**
 * Holds the admin session. Signing in exchanges the seeded admin email and
 * password for the admin key, which is what every later call sends. There is
 * no server session — the key in sessionStorage is the credential — so on
 * mount we re-validate any stored key via {@code whoami} and downgrade to
 * anonymous if it's no longer accepted.
 */
export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(adminKey.get() ? "checking" : "anon");
  // Login and whoami also (re)issue the admin's session cookie; tell the rest
  // of the site so the sidebar and public pages know private items are shown.
  const { refresh } = useSession();

  useEffect(() => {
    if (!adminKey.get()) return;
    let cancelled = false;
    adminApi
      .whoami()
      .then(() => {
        if (!cancelled) setStatus("authed");
        void refresh();
      })
      .catch(() => {
        if (cancelled) return;
        adminKey.clear();
        setStatus("anon");
      });
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(async (email: string, password: string) => {
    const session = await adminApi.login(email, password); // throws ApiError on bad credentials
    if (!session.adminKey) throw new Error("Sign-in succeeded but no admin key was returned.");
    adminKey.set(session.adminKey);
    setStatus("authed");
    void refresh();
  }, [refresh]);

  const logout = useCallback(() => {
    adminKey.clear();
    setStatus("anon");
    // End the cookie session too, or the public site would keep showing
    // private items in this browser after "Sign out".
    void adminApi
      .logout()
      .catch(() => undefined)
      .then(() => refresh());
  }, [refresh]);

  return <Ctx.Provider value={{ status, login, logout }}>{children}</Ctx.Provider>;
}

export function useAdminAuth(): AdminAuthValue {
  const value = useContext(Ctx);
  if (!value) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return value;
}
